import { Avatar, Box, Typography } from "@mui/material";
import { BuildingsIcon, UserCircleIcon } from "@phosphor-icons/react";
import { CalendarToday, AccessTime } from "@mui/icons-material";
import { IMAGES_PATH } from "../constants";

// Reusable row component
const Row = ({ icon, text, iconSize = 21 }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
            sx={{
                width: iconSize,
                height: iconSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0,0,0,0.35)",
            }}
        >
            {icon}
        </Box>
        <Typography
            sx={{
                fontFamily: "IBM Plex Mono",
                fontSize: 13.6,
                fontWeight: 600,
                color: "#2C2A29",
                lineHeight: "21px",
                letterSpacing: "-0.02em",
            }}
        >
            {text}
        </Typography>
    </Box>
);

// Single Card
const Card = ({ data }) => (
    <Box
        sx={{
            width: "100%",
            maxWidth: 270,
            background: "#fff",
            borderRadius: 3.5, // 29px
            border: "5px solid rgba(24, 127, 210, .2)",
            px: 2.5,
            pt: 2.5,
            pb: 2,
            display: "flex",
            flexDirection: "column",
        }}
    >
        <Avatar
            src={data.avatar}
            alt={data.name}
            sx={{ width: 75, height: 75, mb: 2 }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Row
                icon={<UserCircleIcon size={24} weight="duotone" />}
                text={`${data.name}, ${data.role}`}
            />
            <Row
                icon={<BuildingsIcon size={24} weight="duotone" />}
                text={data.company}
            />
            <Row icon={<CalendarToday sx={{ fontSize: 18 }} />} text={data.date} />
            <Row icon={<AccessTime sx={{ fontSize: 18 }} />} text={data.time} />
        </Box>
    </Box>
);

// Background with content
const AuthBGImage = () => (
    <Box
        sx={{
            height: "100%",
            width: "100%",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "linear-gradient(180deg, #0072CE 0%, #CEE9FF 100%)",
            pb: 4
        }}
    >
        {/* Grid overlay */}
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `
          linear-gradient(rgba(206,233,255,0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(206,233,255,0.5) 1px, transparent 1px)
        `,
                backgroundSize: "40px 40px",
                opacity: 0.4,
                filter: "blur(2px)",
            }}
        />

        {/* Content */}
        <Box
            sx={{
                width: "100%",
                maxWidth: 720,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: 4,
            }}
        >
            <Typography
                sx={{
                    textAlign: "center",
                    color: "#fff",
                    fontSize: "clamp(28px, 2.5vw, 36px)",
                    lineHeight: 1.2,
                    my: 3,
                    fontFamily: "Helvetica Neue",
                }}
            >
                Connecting vetted vendors with active CISOs through 1:1 meetings
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: { xs: 2, xl: 4 },
                    position: "relative",
                }}
            >
                {/* LEFT COLUMN */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                    }}
                >
                    <Card data={cardData[0]} />
                    <Card data={cardData[1]} />
                </Box>

                {/* RIGHT COLUMN (staggered) */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        mt: 6, // subtle vertical offset for stagger
                    }}
                >
                    <Card data={cardData[2]} />
                    <Card data={cardData[3]} />
                </Box>
            </Box>
        </Box>

        {/* Gradient overlay */}
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background: `
          linear-gradient(
            180deg,
            rgba(255,255,255,0.08) 0%,
            rgba(220,235,255,0.18) 30%,
            rgba(200,225,255,0.35) 60%,
            rgba(185,215,250,0.5) 85%,
            rgba(185,215,250,0.6) 100%
          )
        `,
            }}
        />
    </Box>
);

export default AuthBGImage;

const cardData = [
    {
        name: "Craig Clark",
        role: "VP of Global Sales",
        company: "Entro Security",
        date: "August 30, 2025",
        time: "8:45 AM - 09:30 AM",
        avatar: `${IMAGES_PATH}/vendor1.jpeg`,
    },

    {
        name: "Chris Holden",
        role: "Senior Vice President & CISO",
        company: "Crum & Forster",
        date: "September 5, 2025",
        time: "1:15 PM - 2:00 PM",
        avatar: `${IMAGES_PATH}/ambassador1.jpeg`,
    },
    {
        name: "Shira Zeltner Weiss",
        role: "Head of GTM & Operations",
        company: "Aryon Security",
        date: "September 2, 2025",
        time: "10:00 AM - 10:45 AM",
        avatar: `${IMAGES_PATH}/vendor2.jpeg`,
    },
    {
        name: "Tal Hornstein",
        role: "Chief Information Security Officer",
        company: "Cast & Crew",
        date: "September 8, 2025",
        time: "3:30 PM - 4:15 PM",
        avatar: `${IMAGES_PATH}/ambassador2.jpeg`,
    },
];